// src/pages/admin/components/WebsiteProjectTab.jsx
import { useEffect, useState } from 'react'
import { ExternalLink, RefreshCw } from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import WebsiteDeliveryPanel from './WebsiteDeliveryPanel'

// Statuses where there is a repo to publish, a build to watch, or a site to
// point a domain at. 'failed' is included because a failed DEPLOY is retried
// from the delivery panel, not by regenerating the scaffold.
const DELIVERY_STATUSES = ['done', 'deploying', 'preview_ready', 'changes_requested', 'approved', 'live', 'failed']
const hasDelivery = (p) => Boolean(p && DELIVERY_STATUSES.includes(p.status) && p.repo_name)

export default function WebsiteProjectTab({ client }) {
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [error, setError] = useState(null)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => { loadProject() }, [client.id])

  // Load via the admin server endpoint (service role) rather than a direct
  // browser read: website_projects RLS only lets a client read its OWN row, so
  // an admin viewing a client's profile reads zero rows. Going through
  // requireAdmin mirrors the ?action=status poll below and is what makes the
  // re-read after Activate actually surface the new project.
  //
  // 404 is the ONLY "no project yet" signal. Any other failure (401/403/5xx,
  // network, malformed body) must NOT null out the project — otherwise a
  // transient error renders the "Activate" empty state and invites a duplicate
  // activation. On such failures we keep the current project and raise
  // loadError so the UI can show a retry instead of the Activate CTA.
  async function loadProject() {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`/api/website?action=get-project&client_id=${client.id}`, {
        headers: { Authorization: `Bearer ${session?.access_token ?? ''}` },
      })
      if (res.status === 404) { setProject(null); setLoadError(null); return }  // no project yet — expected
      const data = await res.json().catch(() => null)
      if (!res.ok) {
        setLoadError((data && (data.message || data.error)) || `Could not load project (${res.status})`)
        return  // leave existing `project` untouched
      }
      setProject(data); setLoadError(null)
    } catch {
      setLoadError('Could not load website project — check your connection and retry.')
    } finally {
      setLoading(false)
    }
  }

  // Poll while generating/pending
  useEffect(() => {
    if (!project) return
    if (project.status !== 'generating' && project.status !== 'pending') return

    let cancelled = false
    let timer

    async function poll() {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const res = await fetch(`/api/website?action=status&id=${project.id}`, {
          headers: { Authorization: `Bearer ${session?.access_token ?? ''}` },
        })
        const data = await res.json()
        if (cancelled) return
        setProject(prev => ({ ...prev, ...data }))

        if (data.status === 'done' || data.status === 'failed') {
          setWorking(false)
          return
        }
        timer = setTimeout(poll, 3000)
      } catch {
        if (!cancelled) timer = setTimeout(poll, 3000 * 2)
      }
    }

    timer = setTimeout(poll, 3000)
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
    // Only id+status drive polling lifecycle; including `project` would restart the timer on every poll's setProject.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.status])

  async function activate() {
    setWorking(true); setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/website?action=activate', {
        method:'POST',
        headers:{ Authorization:`Bearer ${session.access_token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ client_id: client.id }),
      })
      const j = await res.json()
      if (!res.ok) throw new Error(j.message || j.error)
      await loadProject()
    } catch (e) { setError(e.message) } finally { setWorking(false) }
  }

  async function startScaffold() {
    setWorking(true); setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/website?action=start', {
        method:'POST',
        headers:{ Authorization:`Bearer ${session.access_token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ project_id: project.id }),
      })
      const j = await res.json()
      if (!res.ok) throw new Error(j.message || j.error)
      await loadProject()
    } catch (e) { setError(e.message); setWorking(false) }
  }

  if (loading) return <p style={{ color:'#94A3B8' }}>Loading…</p>

  // Load failed (not a 404). Show a retry rather than the Activate empty state,
  // so a transient error can't be mistaken for "no project" and trigger a
  // duplicate activation.
  if (loadError && !project) {
    return (
      <div style={{ padding: 20 }}>
        <h3 style={h3}>Website project</h3>
        <p style={errStyle}>{loadError}</p>
        <button onClick={loadProject} disabled={loading} style={btnPrimary}>
          <RefreshCw size={14} style={{ marginRight: 6 }} /> Retry
        </button>
      </div>
    )
  }

  // No project yet
  if (!project) {
    return (
      <div style={{ padding: 20 }}>
        <h3 style={h3}>Website project</h3>
        <p style={p}>No website project for this client yet. Activate one to send the intake form to their portal.</p>
        <button onClick={activate} disabled={working} style={btnPrimary}>
          {working ? 'Activating…' : 'Activate Website Project'}
        </button>
        {error && <p style={errStyle}>{error}</p>}
      </div>
    )
  }

  return (
    <div style={{ padding: 20, display:'flex', flexDirection:'column', gap: 18 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h3 style={h3}>Website project</h3>
        <span style={badge(project.status)}>{project.status}</span>
      </div>

      {project.status === 'intake_pending' && (
        <p style={p}>Awaiting client intake. They have a link to fill the form in their portal.</p>
      )}

      {project.status === 'intake_submitted' && project.inputs && (
        <>
          <IntakePreview inputs={project.inputs} templateId={project.template_id} />
          <button onClick={startScaffold} disabled={working} style={btnPrimary}>
            {working ? 'Generating…' : 'Generate Scaffold'}
          </button>
        </>
      )}

      {project.status === 'generating' && (
        <div style={{ background:'rgba(0,207,255,0.06)', border:'1px solid rgba(0,207,255,0.2)', borderRadius: 10, padding: 14 }}>
          <p style={{ color:'#00CFFF', fontSize: 13, margin: 0 }}>{project.progress_message || 'Generating…'}</p>
        </div>
      )}

      {/* Deploy → preview → approve → live. Shown from `done` onward, which is
          where the funnel used to stop. */}
      {hasDelivery(project) && (
        <WebsiteDeliveryPanel project={project} onRefresh={loadProject} />
      )}

      {project.repo_url && (
        <div>
          <a href={project.repo_url} target="_blank" rel="noreferrer" style={btnGhost}>
            <ExternalLink size={14} style={{ marginRight: 6 }} /> View on GitHub
          </a>
          {project.ai_content && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ color:'#94A3B8', fontSize: 12, cursor:'pointer' }}>Generated content</summary>
              <pre style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: 12, color:'#CBD5E1', fontSize: 11, overflow:'auto', marginTop: 8 }}>{JSON.stringify(project.ai_content, null, 2)}</pre>
            </details>
          )}
        </div>
      )}

      {project.status === 'failed' && (
        <>
          <p style={errStyle}>{project.error || 'Generation failed'}</p>
          {/* Two different failures land here. Without a repo the scaffold never
              finished, so the retry is Generate. With a repo it was the deploy
              that failed, and the delivery panel above has the retry. */}
          {!project.repo_name && (
            <button onClick={startScaffold} disabled={working} style={btnPrimary}>
              <RefreshCw size={14} style={{ marginRight: 6 }} /> Retry generation
            </button>
          )}
        </>
      )}

      {error && <p style={errStyle}>{error}</p>}
    </div>
  )
}

function IntakePreview({ inputs, templateId }) {
  return (
    <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: 14 }}>
      <Row label="Template"  value={templateId} />
      <Row label="Domain"    value={inputs.domain} />
      <Row label="Pages"     value={(inputs.pages || []).join(', ')} />
      <Row label="Services"  value={(inputs.services || []).join(', ')} />
      <Row label="Brand kit" value={inputs.use_brand_kit ? 'Yes' : 'No'} />
      <div style={{ marginTop: 8 }}>
        <div style={{ color:'#94A3B8', fontSize: 11, marginBottom: 4 }}>Description</div>
        <div style={{ color:'#CBD5E1', fontSize: 12 }}>{inputs.business_description}</div>
      </div>
      {inputs.color_style_prefs && (
        <div style={{ marginTop: 8 }}>
          <div style={{ color:'#94A3B8', fontSize: 11, marginBottom: 4 }}>Style preferences</div>
          <div style={{ color:'#CBD5E1', fontSize: 12 }}>{inputs.color_style_prefs}</div>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div style={{ display:'flex', gap: 12, padding: '4px 0' }}>
      <div style={{ color:'#94A3B8', fontSize: 12, width: 90 }}>{label}</div>
      <div style={{ color:'#F1F5F9', fontSize: 12 }}>{value}</div>
    </div>
  )
}

const h3 = { color:'#F1F5F9', fontSize: 14, fontWeight: 700, margin: 0 }
const p = { color:'#CBD5E1', fontSize: 13 }
const errStyle = { color:'#F87171', fontSize: 13 }
const btnPrimary = { background:'#00CFFF', color:'#0F172A', border:'none', borderRadius: 8, padding:'8px 14px', fontWeight: 700, fontSize: 12, cursor:'pointer', textDecoration:'none', display:'inline-flex', alignItems:'center' }
const btnGhost = { ...btnPrimary, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.12)', color:'#F1F5F9' }
// `live` is the only green: 'done' used to read as finished, and it is not —
// it means generated, nothing deployed, client has seen nothing.
const GREEN = ['live']
const AMBER = ['changes_requested']
const badge = (s) => {
  const [bg, fg] =
    GREEN.includes(s) ? ['rgba(34,197,94,0.1)', '#4ADE80']
    : AMBER.includes(s) ? ['rgba(245,158,11,0.1)', '#FCD34D']
    : s === 'failed' ? ['rgba(239,68,68,0.1)', '#F87171']
    : ['rgba(0,207,255,0.08)', '#00CFFF']
  return {
    background: bg, color: fg, border: '1px solid currentColor',
    borderRadius: 100, padding: '4px 10px', fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  }
}
